import BaseMap from "@arcgis/core/Basemap";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import Map from "@arcgis/core/Map";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import MapView from "@arcgis/core/views/MapView";
import { TargetedEvent } from "@arcgis/map-components";
import {
  ARCGIS_BASEMAP_ID_BY_MAP_STYLE,
  getMarkerSymbol,
  WORLD_MAP_FOCUS_ZOOM,
} from "@components/world-map/world-map-constants";
import {
  MapStyle,
  WorldMapEntity,
  WorldMapGeoJSONLayerField,
  WorldMapView,
} from "@components/world-map/world-map-types";
import {
  getLayer,
  getWebMercatorGeometries,
} from "@components/world-map/world-map-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface UseMapInitializationReturn {
  /** Ref to the ArcGIS MapView instance. Null until the view-ready event fires. */
  viewRef: RefObject<MapView | null>;

  /** Ref to the GeoJSONLayer added to the map. Null until the layer is created. */
  layerRef: RefObject<GeoJSONLayer | null>;

  /** Whether the ArcGIS view has been initialized and is ready to use. */
  isViewReady: boolean;

  /** Handler to pass to the `onarcgisViewReadyChange` event of `<arcgis-map>`. */
  handleViewReady: (
    event: TargetedEvent<HTMLArcgisMapElement, void>
  ) => Promise<void>;
}

interface Props<T extends WorldMapEntity> {
  /** Array of map entities to render as markers. */
  entities: T[];

  /** GeoJSON layer field definitions for the entity type. */
  fields: WorldMapGeoJSONLayerField<T>[];

  /** Visual style of the basemap. Re-applied when the value changes. */
  mapStyle: MapStyle;

  /** Optional initial center/zoom that overrides fitting the view to the entities. */
  initialView?: WorldMapView;
}

/**
 * Initializes and manages the ArcGIS Map and GeoJSONLayer lifecycle.
 * Loads the basemap style, creates the layer from the provided entities,
 * updates the layer when entities change, and destroys the map on unmount.
 *
 * @returns Refs and handlers needed to wire up the `<arcgis-map>` web component.
 */
export function useMapInitialization<T extends WorldMapEntity>({
  entities,
  fields,
  mapStyle,
  initialView,
}: Props<T>): UseMapInitializationReturn {
  const { handleErrorWithToast } = useErrorContext();
  const [isViewReady, setIsViewReady] = useState(false);
  const mapRef = useRef<Map | null>(null);
  const viewRef = useRef<MapView | null>(null);
  const layerRef = useRef<GeoJSONLayer | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const mapStyleRef = useRef<MapStyle>(mapStyle);

  // LODs captured from the initial basemap (gray-vector). Forced on every
  // basemap swap so all styles share the exact same zoom levels.
  const baseLODsRef = useRef<__esri.LOD[] | null>(null);

  /**
   * Builds a fresh GeoJSON layer configured for custom popup handling.
   */
  const createConfiguredLayer = useCallback((): GeoJSONLayer => {
    const { layer: nextLayer, blobUrl: nextBlobUrl } = getLayer({
      entities,
      fields,
      mapStyle: mapStyleRef.current,
    });

    blobUrlRef.current = nextBlobUrl;
    nextLayer.popupEnabled = false;

    return nextLayer;
  }, [entities, fields]);

  /**
   * Loads and applies the named ArcGIS basemap for the given style.
   * Awaits basemap.load() so callers can render markers only after the basemap is ready.
   */
  const setupMapBasemap = useCallback(
    async (style: MapStyle): Promise<void> => {
      if (!mapRef.current) {
        return;
      }

      const fromId = BaseMap.fromId(ARCGIS_BASEMAP_ID_BY_MAP_STYLE[style]);
      if (!fromId) {
        throw new Error(`Unknown ArcGIS basemap id for style "${style}".`);
      }
      // ArcGIS draws `referenceLayers` (roads, labels, boundaries) above
      // operational layers, which would cover our markers on the `hybrid`
      // basemap. Rebuild the basemap with every layer in `baseLayers` so
      // markers always render on top while labels stay visible.
      await fromId.load();
      const nextBasemap = new BaseMap({
        baseLayers: [
          ...fromId.baseLayers.toArray(),
          ...fromId.referenceLayers.toArray(),
        ],
      });

      mapRef.current.basemap = nextBasemap;
      await nextBasemap.load();

      // basemap.load() only loads metadata; tiles still need to render.
      // Wait for the view to settle (`view.updating === false`) so callers
      // know the new basemap is actually visible on screen.
      if (viewRef.current) {
        await reactiveUtils.whenOnce(() => viewRef.current?.updating === false);
      }
    },
    []
  );

  /**
   * Removes the previous data layer (if any), revokes its blob URL, and adds a freshly
   * built layer to the map. Used both for the initial setup and whenever the layer
   * needs to be rebuilt (e.g. entities or fields changed).
   */
  const replaceDataLayer = useCallback((): void => {
    if (!mapRef.current) {
      return;
    }

    if (layerRef.current) {
      mapRef.current.remove(layerRef.current);
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }

    layerRef.current = createConfiguredLayer();
    mapRef.current.add(layerRef.current);
  }, [createConfiguredLayer]);

  /**
   * Sets up the initial map view.
   * If an `initialView` is provided (e.g. from a deep link), it takes precedence and the
   * view is centered on it with the requested zoom level.
   * Otherwise the view is derived from the entities:
   * If there are no entities, early exit and use the default basemap view.
   * If there is one entity, set the view to that entity with a focus zoom level.
   * If there are multiple entities, set the view to encompass all entities.
   */
  async function setupView(): Promise<void> {
    // Skip animation since on map initialization we want to show the map in the correct position immediately
    const goToOptions: __esri.GoToOptions2D = {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API
      animate: false,
    };

    if (initialView) {
      await viewRef.current?.goTo(
        {
          center: [initialView.lng, initialView.lat],
          zoom: initialView.zoom,
        },
        goToOptions
      );
      return;
    }

    const geometries = getWebMercatorGeometries(entities);

    if (geometries.length === 0) {
      return;
    }

    if (geometries.length === 1) {
      return await viewRef.current?.goTo(
        {
          target: geometries[0],
          zoom: WORLD_MAP_FOCUS_ZOOM,
        },
        goToOptions
      );
    }

    await viewRef.current?.goTo(geometries, goToOptions);
  }

  /**
   * Handles the ArcGIS view-ready event and performs initial map setup.
   */
  async function handleViewReady(
    event: TargetedEvent<HTMLArcgisMapElement, void>
  ): Promise<void> {
    try {
      const { view } = event.target;

      if (!view.map) {
        throw new Error("Error loading the map instance.");
      }

      viewRef.current = view;
      mapRef.current = view.map;

      // Capture the LODs from the initial basemap (gray-vector) so we can
      // force them on all subsequent basemaps. This guarantees all map styles
      // (colored, satellite) share the exact same set of zoom levels.
      baseLODsRef.current = view.constraints.effectiveLODs
        ? [...view.constraints.effectiveLODs]
        : null;

      // Force integer zoom levels so zoom-out behavior is consistent across
      // basemap styles (satellite supports fractional zoom, colored does not).
      view.constraints = {
        ...view.constraints,
        /* eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API */
        snapToZoom: true,
      };

      // Load the view and basemap first, then add the data layer so markers appear on top
      // of a fully-loaded basemap rather than over a blank/old background.
      await setupView();
      await setupMapBasemap(mapStyle);

      // Re-apply the LODs from the initial basemap after the style switch
      // to ensure the new basemap doesn't introduce extra zoom levels.
      if (baseLODsRef.current) {
        view.constraints = {
          ...view.constraints,
          lods: baseLODsRef.current,
        };
      }

      replaceDataLayer();
      setIsViewReady(true);
    } catch (error) {
      handleErrorWithToast({
        id: `handleViewReady-${Date.now().toString()}`,
        title: "Error initializing the map.",
        error,
      });
    }
  }

  // Recreate the data layer when entities or layer fields change.
  useEffect(() => {
    if (mapRef.current && layerRef.current) {
      replaceDataLayer();
    }
  }, [replaceDataLayer]);

  // Re-apply the basemap and recolor the marker renderer when the requested style changes
  // after the view is ready. Mirrors the initial load order: basemap finishes loading first,
  // then the marker color is updated so the new color appears in sync with the new basemap.
  // The data layer itself is reused (no cluster/feature reset).
  //
  // While the new basemap is loading, ArcGIS clears the old tiles, which would otherwise
  // leave markers floating on a white canvas. We hide the data layer for the duration of
  // the swap and re-show it once the new basemap is ready.
  useEffect(() => {
    // Wait for the map view to be ready before listening for map style changes.
    if (!isViewReady) {
      return;
    }

    // Early exit if the new style is the same as the current/cached style reference.
    // This prevents any unnecessary map re-render when the `mapStyle` prop is updated with
    // the same value or when the map has loaded for the first time and the flag `isViewReady` changes to true.
    if (mapStyleRef.current === mapStyle) {
      return;
    }

    // Update the cached map style reference to the new value.
    mapStyleRef.current = mapStyle;

    let isCancelled = false;
    const dataLayer = layerRef.current;
    if (dataLayer) {
      dataLayer.visible = false;
    }

    void (async () => {
      try {
        await setupMapBasemap(mapStyle);
        if (isCancelled) {
          return;
        }

        // Re-enforce the LODs from the initial basemap and ensure integer
        // zooming is still enabled, so the new basemap cannot introduce extra
        // zoom levels that differ from the colored style.
        if (viewRef.current) {
          viewRef.current.constraints = {
            ...viewRef.current.constraints,
            /* eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API */
            snapToZoom: true,
            lods: baseLODsRef.current ?? undefined,
          };
        }

        if (layerRef.current) {
          layerRef.current.renderer = new SimpleRenderer({
            symbol: getMarkerSymbol(mapStyle),
          });
          layerRef.current.visible = true;
        }
      } catch (error) {
        // Ensure the layer is visible again even if the basemap swap failed,
        // so the user is not left without markers indefinitely.
        if (layerRef.current) {
          layerRef.current.visible = true;
        }
        handleErrorWithToast({
          id: `setupMapBasemap-${Date.now().toString()}`,
          title: "Error switching the map style.",
          error,
        });
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [mapStyle, isViewReady, setupMapBasemap, handleErrorWithToast]);

  // Destroy map resources when the hook owner unmounts.
  useEffect(() => {
    return () => {
      if (viewRef.current?.container) {
        viewRef.current.container.style.cursor = "default";
      }
      mapRef.current?.destroy();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  return {
    viewRef,
    layerRef,
    isViewReady,
    handleViewReady,
  };
}
